
import React, { useEffect, useState } from "react";
import { FaUser } from "react-icons/fa";

const AppointmentsPage = ({
  groupedByPatient,

  expandedDates,
  toggleDateExpand,
  baseUrl,
  otherMedicalUsers,
  setOtherMedicalUsers,
  searchQueries,
  setSearchQueries,
  notifiedTests,
  setNotifiedTests,
  registeredUsers,
  handleNotifyAndSave,
  handleCancelTestRecommendation,
  handleAddPrescription,
  handleStatusChange,
  handleCancelAppointment,
  handleAddTestRecommendation,
  handleSaveToDB,
  fetchRegisteredUsers,
}) => {
  useEffect(() => {}, [otherMedicalUsers]);

  const [deselectedTests, setDeselectedTests] = useState({});

  // ============================================================
  // GROUP APPOINTMENTS BY DATE
  // ============================================================

  const groupedData = Object.entries(
    Object.groupBy(
      Object.values(groupedByPatient || {}).flatMap(
        ({ patient, appointments }) =>
          appointments.map((appt) => ({
            ...appt,
            patient,
            dateKey: new Date(appt.date)
              .toISOString()
              .slice(0, 10),
          })),
      ),
      (appt) => appt.dateKey,
    ),
  ).sort(([a], [b]) => new Date(a) - new Date(b));

  // ============================================================
  // STATUS STYLE
  // ============================================================

  const getStatusStyle = (status) => {
    const normalized = String(status || "pending").toLowerCase();

    switch (normalized) {
      case "confirmed":
        return {
          wrapper:
            "bg-sky-50 text-sky-700 border border-sky-100",
          dot: "bg-sky-500",
        };

      case "completed":
        return {
          wrapper:
            "bg-emerald-50 text-emerald-700 border border-emerald-100",
          dot: "bg-emerald-500",
        };

      case "cancelled":
      case "canceled":
      case "declined":
        return {
          wrapper:
            "bg-red-50 text-red-700 border border-red-100",
          dot: "bg-red-500",
        };

      case "in-progress":
        return {
          wrapper:
            "bg-amber-50 text-amber-700 border border-amber-100",
          dot: "bg-amber-500",
        };

      default:
        return {
          wrapper:
            "bg-amber-50 text-amber-700 border border-amber-100",
          dot: "bg-amber-500",
        };
    }
  };

  // ============================================================
  // PATIENT INITIALS
  // ============================================================

  const getInitials = (name) => {
    if (!name) return "P";

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  // ============================================================
  // FORMAT DATE
  // ============================================================

  const formatDisplayDate = (date) => {
    if (!date) return "N/A";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  // ============================================================
  // FORMAT TIME
  // ============================================================

  const formatDisplayTime = (time) => {
    if (!time) return "N/A";

    const value = String(time).trim();

    if (/am|pm/i.test(value)) {
      return value;
    }

    const parts = value.split(":");

    if (parts.length < 2) {
      return value;
    }

    let hours = Number(parts[0]);
    const minutes = parts[1];

    if (Number.isNaN(hours)) {
      return value;
    }

    const suffix = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;

    return `${String(hours).padStart(2, "0")}:${minutes} ${suffix}`;
  };

  return (
    <section className="w-full my-4 bg-[#eef3f8] min-h-[600px]">

      {/* ========================================================
          PAGE HEADER
      ======================================================== */}

      <div className="px-1 pb-5">
        <h2 className="text-3xl font-bold text-[#0f1f33] tracking-tight">
          Appointments
        </h2>

        <p className="text-sm text-[#6b829d] mt-1">
          Manage patient appointments and consultation details
        </p>
      </div>

      {/* ========================================================
          APPOINTMENT DATES
      ======================================================== */}

      {groupedData.map(([date, apptsOnDate]) => {
        const isExpanded = expandedDates[date];

        // ======================================================
        // STATUS COUNTS FOR THIS DATE
        // ======================================================

        const statusCounts = apptsOnDate.reduce(
          (acc, appt) => {
            const status = String(
              appt.status || "pending",
            ).toLowerCase();

            acc[status] = (acc[status] || 0) + 1;

            return acc;
          },
          {},
        );

        const confirmedCount =
          statusCounts.confirmed || 0;

        const pendingCount =
          statusCounts.pending || 0;

        const inProgressCount =
          statusCounts["in-progress"] || 0;

        const completedCount =
          statusCounts.completed || 0;

        const cancelledCount =
          (statusCounts.cancelled || 0) +
          (statusCounts.canceled || 0) +
          (statusCounts.declined || 0);

        return (
          <div key={date} className="mb-7">

            {/* ==================================================
                DATE + STATUS SUMMARY RIBBON
            ================================================== */}

            <div
              onClick={() => toggleDateExpand(date)}
              className="w-full bg-white border border-[#d5e0eb] rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(15,31,51,0.04)] hover:shadow-[0_4px_14px_rgba(15,31,51,0.08)] hover:border-[#b9cddd] transition-all duration-200 cursor-pointer"
            >

              {/* =================================================
                  TOP RIBBON
              ================================================= */}

              <div className="px-4 py-3">

                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">

                  {/* DATE */}

                  <div className="flex items-center gap-3 min-w-0">

                    <div className="w-10 h-10 rounded-lg bg-[#193a5c] text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-lg">
                        📅
                      </span>
                    </div>

                    <div className="min-w-0">

                      <p className="text-[10px] uppercase tracking-wide font-bold text-[#8194a8]">
                        Appointment Date
                      </p>

                      <h3 className="text-base font-bold text-[#193a5c] whitespace-nowrap">
                        {formatDisplayDate(date)}
                      </h3>

                    </div>
                  </div>

                  {/* =================================================
                      STATUS SUMMARY
                  ================================================= */}

                  <div className="flex flex-wrap items-center gap-2">

                    {/* ALL */}

                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#193a5c] text-white text-[11px] font-bold whitespace-nowrap shadow-sm">

                      <span className="w-1.5 h-1.5 rounded-full bg-white" />

                      All

                      <span className="opacity-80">
                        {apptsOnDate.length}
                      </span>

                    </span>

                    {/* CONFIRMED */}

                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-50 border border-sky-100 text-sky-700 text-[11px] font-bold whitespace-nowrap">

                      <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />

                      Confirmed

                      <span className="text-sky-500">
                        {confirmedCount}
                      </span>

                    </span>

                    {/* PENDING */}

                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-100 text-amber-700 text-[11px] font-bold whitespace-nowrap">

                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />

                      Pending

                      <span className="text-amber-500">
                        {pendingCount}
                      </span>

                    </span>

                    {/* IN PROGRESS */}

                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100 text-orange-700 text-[11px] font-bold whitespace-nowrap">

                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />

                      In Progress

                      <span className="text-orange-500">
                        {inProgressCount}
                      </span>

                    </span>

                    {/* COMPLETED */}

                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-[11px] font-bold whitespace-nowrap">

                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />

                      Completed

                      <span className="text-emerald-500">
                        {completedCount}
                      </span>

                    </span>

                    {/* CANCELLED */}

                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 border border-red-100 text-red-700 text-[11px] font-bold whitespace-nowrap">

                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />

                      Cancelled

                      <span className="text-red-500">
                        {cancelledCount}
                      </span>

                    </span>

                    {/* EXPAND */}

                    <span
                      className={`ml-1 w-7 h-7 rounded-full bg-[#eef3f8] text-[#45627e] flex items-center justify-center text-xs font-bold transition-transform duration-200 ${
                        isExpanded
                          ? "rotate-180"
                          : ""
                      }`}
                    >
                      ▼
                    </span>

                  </div>
                </div>
              </div>

              {/* =================================================
                  RIBBON BOTTOM LINE
              ================================================= */}

              <div className="h-1 bg-gradient-to-r from-[#193a5c] via-[#3c82b8] to-[#8bc6e8]" />

            </div>

            {/* ==================================================
                APPOINTMENT RIBBONS
            ================================================== */}

            <div className="mt-3 space-y-2">

              {apptsOnDate.map((appt) => {
                const patient = appt.patient;

                const patientName =
                  patient?.name ||
                  patient?.fullName ||
                  "Unknown Patient";

                const status = String(
                  appt.status || "pending",
                ).toLowerCase();

                const statusStyle =
                  getStatusStyle(status);

                const appointmentType =
                  appt.type ||
                  appt.appointmentType ||
                  appt.description ||
                  "Consultation";

                return (
                  <div
                    key={appt.appointmentId}
                    className="w-full bg-white border border-[#d5e0eb] rounded-xl overflow-hidden shadow-[0_1px_5px_rgba(15,31,51,0.03)]"
                  >

                    {/* =================================================
                        APPOINTMENT SUMMARY RIBBON
                    ================================================= */}

                    <div
                      onClick={() =>
                        toggleDateExpand(date)
                      }
                      className="px-4 py-3 cursor-pointer hover:bg-[#f8fbfe] transition-colors"
                    >

                      <div className="flex flex-col xl:flex-row xl:items-center gap-4">

                        {/* PATIENT */}

                        <div className="flex items-center gap-3 min-w-0 xl:w-[32%]">

                          <div className="w-9 h-9 rounded-full bg-[#193a5c] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {getInitials(patientName)}
                          </div>

                          <div className="min-w-0">

                            <p className="text-sm font-bold text-[#102a45] truncate">
                              {patientName}
                            </p>

                            <p className="text-[10px] uppercase tracking-wide font-semibold text-[#8194a8]">
                              Patient
                            </p>

                          </div>
                        </div>

                        {/* TIME */}

                        <div className="flex items-center gap-2 min-w-0 xl:w-[20%]">

                          <div className="w-8 h-8 rounded-lg bg-[#eef4f9] flex items-center justify-center flex-shrink-0">
                            <span className="text-sm">
                              🕐
                            </span>
                          </div>

                          <div>

                            <p className="font-mono text-sm font-semibold text-[#26425e] whitespace-nowrap">
                              {formatDisplayTime(
                                appt.time,
                              )}
                            </p>

                            <p className="text-[10px] uppercase tracking-wide font-semibold text-[#8194a8]">
                              Time
                            </p>

                          </div>
                        </div>

                        {/* TYPE */}

                        <div className="flex items-center gap-2 min-w-0 xl:flex-1">

                          <div className="w-8 h-8 rounded-lg bg-[#eef4f9] flex items-center justify-center flex-shrink-0">
                            <span className="text-sm">
                              🩺
                            </span>
                          </div>

                          <div className="min-w-0">

                            <p className="text-sm font-semibold text-[#26425e] truncate">
                              {appointmentType}
                            </p>

                            <p className="text-[10px] uppercase tracking-wide font-semibold text-[#8194a8]">
                              Type
                            </p>

                          </div>
                        </div>

                        {/* STATUS */}

                        <div className="flex items-center xl:justify-end xl:w-[18%]">

                          <span
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold capitalize whitespace-nowrap ${statusStyle.wrapper}`}
                          >

                            <span
                              className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}
                            />

                            {status}

                          </span>

                        </div>

                      </div>
                    </div>

                    {/* =================================================
                        APPOINTMENT RIBBON BOTTOM LINE
                    ================================================= */}

                    <div className="h-[2px] bg-[#edf2f7]" />

                  </div>
                );
              })}

            </div>

            {/* ==================================================
                EXPANDED CONTENT
            ================================================== */}

            {isExpanded && (
              <div className="mt-3 w-full bg-white border border-[#cedbea] rounded-2xl overflow-hidden shadow-[0_2px_10px_rgba(15,31,51,0.04)]">

                {/* =================================================
                    TABLE HEADER
                ================================================= */}

                <div className="hidden xl:grid grid-cols-[2fr_1.35fr_1.35fr_2.1fr_1.1fr_1.25fr] bg-white border-b border-[#d4dfeb] px-5">

                  <div className="py-4 text-[11px] font-bold tracking-wide text-[#5d7590] uppercase">
                    Patient
                  </div>

                  <div className="py-4 text-[11px] font-bold tracking-wide text-[#5d7590] uppercase">
                    Date & Time
                  </div>

                  <div className="py-4 text-[11px] font-bold tracking-wide text-[#5d7590] uppercase">
                    Type
                  </div>

                  <div className="py-4 text-[11px] font-bold tracking-wide text-[#5d7590] uppercase">
                    Notes
                  </div>

                  <div className="py-4 text-[11px] font-bold tracking-wide text-[#5d7590] uppercase">
                    Status
                  </div>

                  <div className="py-4 text-[11px] font-bold tracking-wide text-[#5d7590] uppercase">
                    Action
                  </div>

                </div>

                {/* =================================================
                    APPOINTMENT ROWS
                ================================================= */}

                {apptsOnDate.map((appt) => {
                  const patient = appt.patient;

                  const patientName =
                    patient?.name ||
                    patient?.fullName ||
                    "Unknown Patient";

                  const status = String(
                    appt.status || "pending",
                  ).toLowerCase();

                  const statusStyle =
                    getStatusStyle(status);

                  const testRecommendations =
                    appt.testRecommendations || [];

                  const activeTests =
                    testRecommendations.filter(
                      (test) => !test?.isCancelled,
                    );

                  return (
                    <div
                      key={appt.appointmentId}
                      className="border-b last:border-b-0 border-[#d8e2ed] hover:bg-[#f8fbfe] transition-colors"
                    >

                      {/* =================================================
                          DESKTOP TABLE ROW
                      ================================================= */}

                      <div className="hidden xl:grid grid-cols-[2fr_1.35fr_1.35fr_2.1fr_1.1fr_1.25fr] items-center px-5 min-h-[94px]">

                        {/* PATIENT */}

                        <div className="flex items-center gap-3 min-w-0">

                          <div className="w-9 h-9 rounded-full bg-[#193a5c] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {getInitials(patientName)}
                          </div>

                          <div className="min-w-0">

                            <p className="text-sm font-semibold text-[#102a45] truncate">
                              {patientName}
                            </p>

                            {patient?.age && (
                              <p className="text-xs text-[#71869d]">
                                Age {patient.age}
                              </p>
                            )}

                            {patient?.email && (
                              <p className="text-xs text-[#8a9bad] truncate max-w-[180px]">
                                {patient.email}
                              </p>
                            )}

                          </div>
                        </div>

                        {/* DATE & TIME */}

                        <div>

                          <p className="text-sm font-medium text-[#263f59]">
                            {formatDisplayDate(
                              appt.date,
                            )}
                          </p>

                          <p className="font-mono text-xs text-[#5d7590] mt-0.5">
                            {formatDisplayTime(
                              appt.time,
                            )}
                          </p>

                        </div>

                        {/* TYPE */}

                        <div>

                          <p className="text-sm text-[#203b56] font-medium">
                            {appt.type ||
                              appt.appointmentType ||
                              appt.description ||
                              "Consultation"}
                          </p>

                        </div>

                        {/* NOTES */}

                        <div className="pr-4">

                          {appt.description ? (
                            <p className="text-xs text-[#688099] line-clamp-2">
                              {appt.description}
                            </p>
                          ) : activeTests.length > 0 ? (
                            <p className="text-xs text-[#688099]">
                              {activeTests.length} test recommendation
                              {activeTests.length !==
                              1
                                ? "s"
                                : ""}
                            </p>
                          ) : (
                            <p className="text-xs text-[#9aabba]">
                              No additional notes
                            </p>
                          )}

                        </div>

                        {/* STATUS */}

                        <div>

                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold capitalize ${statusStyle.wrapper}`}
                          >

                            <span
                              className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}
                            />

                            {status}

                          </span>

                        </div>

                        {/* ACTION */}

                        <div>

                          <button
                            type="button"
                            onClick={() =>
                              toggleDateExpand(
                                date,
                              )
                            }
                            className="px-4 py-2 rounded-full bg-[#eaf1f8] text-[#234a70] text-xs font-semibold hover:bg-[#dce8f3] transition"
                          >
                            View
                          </button>

                        </div>

                      </div>

                      {/* =================================================
                          MOBILE / TABLET CARD
                      ================================================= */}

                      <div className="xl:hidden p-4">

                        <div className="flex items-start justify-between gap-3">

                          <div className="flex items-center gap-3">

                            <div className="w-10 h-10 rounded-full bg-[#193a5c] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {getInitials(
                                patientName,
                              )}
                            </div>

                            <div>

                              <h3 className="text-sm font-bold text-[#102a45]">
                                {patientName}
                              </h3>

                              {patient?.age && (
                                <p className="text-xs text-[#71869d]">
                                  Age {patient.age}
                                </p>
                              )}

                              <p className="text-xs text-[#71869d]">
                                {patient?.phone ||
                                  "No phone"}
                              </p>

                            </div>

                          </div>

                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold capitalize ${statusStyle.wrapper}`}
                          >

                            <span
                              className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}
                            />

                            {status}

                          </span>

                        </div>

                        {/* DETAILS */}

                        <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-[#e1e8f0]">

                          <div>

                            <p className="text-[10px] uppercase tracking-wide font-bold text-[#8093a7]">
                              Token
                            </p>

                            <p className="text-sm font-semibold text-[#26425e]">
                              {appt.tokenNumber ||
                                "N/A"}
                            </p>

                          </div>

                          <div>

                            <p className="text-[10px] uppercase tracking-wide font-bold text-[#8093a7]">
                              Date
                            </p>

                            <p className="text-sm font-semibold text-[#26425e]">
                              {formatDisplayDate(
                                appt.date,
                              )}
                            </p>

                          </div>

                          <div>

                            <p className="text-[10px] uppercase tracking-wide font-bold text-[#8093a7]">
                              Time
                            </p>

                            <p className="font-mono text-sm text-[#26425e]">
                              {formatDisplayTime(
                                appt.time,
                              )}
                            </p>

                          </div>

                          <div>

                            <p className="text-[10px] uppercase tracking-wide font-bold text-[#8093a7]">
                              Type
                            </p>

                            <p className="text-sm text-[#26425e]">
                              {appt.type ||
                                appt.appointmentType ||
                                "Consultation"}
                            </p>

                          </div>

                        </div>

                        {appt.description && (
                          <div className="mt-3">

                            <p className="text-[10px] uppercase tracking-wide font-bold text-[#8093a7]">
                              Notes
                            </p>

                            <p className="text-sm text-[#637b93] mt-1">
                              {appt.description}
                            </p>

                          </div>
                        )}

                      </div>

                      {/* =================================================
                          FULL DETAILS / ACTION AREA
                      ================================================= */}

                      <div className="px-5 pb-5">

                        <div className="bg-[#f8fafc] border border-[#e0e7ef] rounded-xl p-4">

                          {/* PRESCRIPTIONS */}

                          <div className="mb-4">

                            <p className="text-xs font-bold text-[#4c6680] uppercase tracking-wide">
                              Prescriptions
                            </p>

                            {Array.isArray(
                              appt.prescriptions,
                            ) ? (
                              appt.prescriptions.length >
                              0 ? (
                                <ul className="mt-2 space-y-1">

                                  {appt.prescriptions.map(
                                    (
                                      presc,
                                      index,
                                    ) => (
                                      <li
                                        key={
                                          index
                                        }
                                        className="text-sm text-emerald-700 font-medium bg-emerald-50 px-3 py-1.5 rounded-lg"
                                      >
                                        {presc}
                                      </li>
                                    ),
                                  )}

                                </ul>
                              ) : (
                                <p className="text-xs text-[#8a9bad] mt-2">
                                  No prescriptions
                                  available
                                </p>
                              )
                            ) : (
                              <p className="text-xs text-red-500 mt-2">
                                Invalid prescription
                                format
                              </p>
                            )}

                          </div>

                          {/* =================================================
                              TEST RECOMMENDATIONS
                          ================================================= */}

                          {activeTests.length >
                            0 && (
                            <div className="mb-4">

                              <p className="text-xs font-bold text-[#4c6680] uppercase tracking-wide mb-3">
                                Test Recommendations
                              </p>

                              <div className="space-y-3">

                                {activeTests.map(
                                  (
                                    test,
                                    index,
                                  ) => {

                                    const testKey =
                                      test.testid ||
                                      `test-${index}`;

                                    const testStatus =
                                      (
                                        test.status ||
                                        "pending"
                                      ).toLowerCase();

                                    const isCompleted =
                                      testStatus ===
                                      "completed";

                                    const selectedUser =
                                      otherMedicalUsers[
                                        testKey
                                      ] ??
                                      test.OtherMedicalUser ??
                                      null;

                                    const isNotified =
                                      notifiedTests[
                                        testKey
                                      ];

                                    const query =
                                      (
                                        searchQueries[
                                          testKey
                                        ] ||
                                        ""
                                      )
                                        .toLowerCase()
                                        .trim();

                                    const shouldSearch =
                                      query.length >=
                                      3;

                                    const filteredUsers =
                                      shouldSearch
                                        ? registeredUsers?.filter(
                                            (
                                              user,
                                            ) =>
                                              user.fullName
                                                ?.toLowerCase()
                                                .includes(
                                                  query,
                                                ) ||
                                              user.email
                                                ?.toLowerCase()
                                                .includes(
                                                  query,
                                                ) ||
                                              user.phone
                                                ?.toString()
                                                .includes(
                                                  query,
                                                ) ||
                                              user.username
                                                ?.toLowerCase()
                                                .includes(
                                                  query,
                                                ),
                                          )
                                        : [];

                                    return (
                                      <div
                                        key={
                                          testKey
                                        }
                                        className="bg-white border border-[#dce5ee] rounded-xl p-4"
                                      >

                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">

                                          <div>

                                            <p className="text-sm font-semibold text-[#23405d]">
                                              🧪{" "}
                                              {test.testType ||
                                                "Test"}
                                            </p>

                                            <p className="text-xs text-[#71869d] mt-1">
                                              Doctor Notes:{" "}
                                              {test.testType ||
                                                "—"}
                                            </p>

                                            <p className="text-xs text-[#71869d] mt-1">
                                              Lab Notes:{" "}
                                              {test.labDescription ||
                                                "—"}
                                            </p>

                                          </div>

                                          <span
                                            className={`self-start px-2.5 py-1 rounded-full text-[10px] font-semibold capitalize ${
                                              isCompleted
                                                ? "bg-emerald-50 text-emerald-700"
                                                : testStatus ===
                                                    "cancelled"
                                                  ? "bg-red-50 text-red-600"
                                                  : "bg-amber-50 text-amber-700"
                                            }`}
                                          >
                                            {
                                              testStatus
                                            }
                                          </span>

                                        </div>

                                        {/* FILES */}

                                        {test.labFiles
                                          ?.length >
                                          0 && (
                                          <div className="mt-3">

                                            <p className="text-xs font-semibold text-[#536b83] mb-2">
                                              📎 Files
                                            </p>

                                            <div className="flex gap-2 flex-wrap">

                                              {test.labFiles.map(
                                                (
                                                  file,
                                                  i,
                                                ) => {

                                                  const BASE_URL =
                                                    process
                                                      .env
                                                      .REACT_APP_BACKEND_BASE_URL;

                                                  const url = `${BASE_URL}${file.fileUrl}`;

                                                  const fileName =
                                                    file.fileUrl
                                                      ?.split(
                                                        "/",
                                                      )
                                                      .pop() ||
                                                    "file";

                                                  const extension =
                                                    fileName
                                                      .split(
                                                        ".",
                                                      )
                                                      .pop()
                                                      ?.toLowerCase();

                                                  const isImage =
                                                    [
                                                      "jpg",
                                                      "jpeg",
                                                      "png",
                                                      "gif",
                                                      "webp",
                                                    ].includes(
                                                      extension,
                                                    );

                                                  return (
                                                    <div
                                                      key={
                                                        i
                                                      }
                                                      className="text-center border border-[#dce5ee] p-2 rounded-lg bg-white w-24"
                                                    >

                                                      {isImage ? (
                                                        <a
                                                          href={
                                                            url
                                                          }
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                        >

                                                          <img
                                                            src={
                                                              url
                                                            }
                                                            alt="lab"
                                                            className="w-20 h-20 object-cover rounded-md border border-[#dce5ee]"
                                                          />

                                                        </a>
                                                      ) : (
                                                        <div className="w-20 h-20 flex items-center justify-center border rounded-md bg-[#f1f5f9] text-xs text-[#61758a]">
                                                          {extension?.toUpperCase()}
                                                        </div>
                                                      )}

                                                      <p className="text-xs mt-1 text-[#667d94] truncate">
                                                        {
                                                          fileName
                                                        }
                                                      </p>

                                                      <div className="flex flex-col gap-1 mt-1">

                                                        <a
                                                          href={
                                                            url
                                                          }
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="text-[11px] text-sky-600 hover:underline"
                                                        >
                                                          View
                                                        </a>

                                                        <a
                                                          href={
                                                            url
                                                          }
                                                          download={
                                                            fileName
                                                          }
                                                          className="bg-[#193a5c] hover:bg-[#102d49] text-white text-[11px] px-1 py-1 rounded-md"
                                                        >
                                                          Download
                                                        </a>

                                                      </div>

                                                    </div>
                                                  );
                                                },
                                              )}

                                            </div>
                                          </div>
                                        )}

                                        {/* SELECTED USER */}

                                        {selectedUser && (
                                          <div className="mt-3 bg-emerald-50 border border-emerald-100 p-3 rounded-lg flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">

                                            <div>

                                              <p className="text-sm font-semibold text-emerald-800">
                                                {
                                                  selectedUser.fullName
                                                }
                                              </p>

                                              <p className="text-xs text-emerald-700">
                                                {
                                                  selectedUser.email
                                                }
                                              </p>

                                              <p className="text-xs text-emerald-700">
                                                {
                                                  selectedUser.phone
                                                }
                                              </p>

                                            </div>

                                            <button
                                              disabled={
                                                isCompleted
                                              }
                                              onClick={() => {

                                                if (
                                                  isCompleted
                                                )
                                                  return;

                                                setOtherMedicalUsers(
                                                  (
                                                    prev,
                                                  ) => ({
                                                    ...prev,
                                                    [testKey]:
                                                      null,
                                                  }),
                                                );

                                                setSearchQueries(
                                                  (
                                                    prev,
                                                  ) => ({
                                                    ...prev,
                                                    [testKey]:
                                                      "",
                                                  }),
                                                );

                                                setNotifiedTests(
                                                  (
                                                    prev,
                                                  ) => {

                                                    const copy =
                                                      {
                                                        ...prev,
                                                      };

                                                    delete copy[
                                                      testKey
                                                    ];

                                                    return copy;
                                                  },
                                                );

                                              }}
                                              className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                                                isCompleted
                                                  ? "bg-gray-400 text-white cursor-not-allowed"
                                                  : "bg-red-500 hover:bg-red-600 text-white"
                                              }`}
                                            >
                                              Deselect
                                            </button>

                                          </div>
                                        )}

                                        {/* SEARCH */}

                                        {!selectedUser && (
                                          <div className="mt-3">

                                            <input
                                              disabled={
                                                isCompleted
                                              }
                                              className={`border border-[#d3dfeb] p-2.5 w-full rounded-lg text-sm outline-none focus:ring-2 focus:ring-sky-100 focus:border-sky-400 ${
                                                isCompleted
                                                  ? "bg-gray-100 cursor-not-allowed"
                                                  : "bg-white"
                                              }`}
                                              placeholder="Search lab user..."
                                              value={
                                                searchQueries[
                                                  testKey
                                                ] ||
                                                ""
                                              }
                                              onChange={(
                                                e,
                                              ) => {

                                                if (
                                                  isCompleted
                                                )
                                                  return;

                                                setSearchQueries(
                                                  (
                                                    prev,
                                                  ) => ({
                                                    ...prev,
                                                    [testKey]:
                                                      e
                                                        .target
                                                        .value,
                                                  }),
                                                );

                                              }}
                                            />

                                            <p className="text-[11px] text-[#8395a8] mt-1">
                                              Type at least
                                              3 characters
                                              to search
                                              lab users
                                            </p>

                                            {shouldSearch &&
                                              !isCompleted && (
                                                <div className="mt-2 max-h-40 overflow-y-auto border border-[#d8e2ec] rounded-lg bg-white shadow-sm">

                                                  {filteredUsers?.length >
                                                  0 ? (
                                                    filteredUsers
                                                      .slice(
                                                        0,
                                                        10,
                                                      )
                                                      .map(
                                                        (
                                                          user,
                                                        ) => (
                                                          <div
                                                            key={
                                                              user._id
                                                            }
                                                            onClick={() => {

                                                              if (
                                                                isCompleted
                                                              )
                                                                return;

                                                              setOtherMedicalUsers(
                                                                (
                                                                  prev,
                                                                ) => ({
                                                                  ...prev,
                                                                  [testKey]:
                                                                    user,
                                                                }),
                                                              );

                                                              setSearchQueries(
                                                                (
                                                                  prev,
                                                                ) => ({
                                                                  ...prev,
                                                                  [testKey]:
                                                                    "",
                                                                }),
                                                              );

                                                            }}
                                                            className="p-3 hover:bg-sky-50 cursor-pointer border-b last:border-b-0 border-[#edf1f5]"
                                                          >

                                                            <p className="font-medium text-sm text-[#23405d]">
                                                              {
                                                                user.fullName
                                                              }
                                                            </p>

                                                            <p className="text-xs text-[#70869d]">
                                                              {
                                                                user.email
                                                              }
                                                            </p>

                                                            <p className="text-xs text-[#91a0af]">
                                                              📱{" "}
                                                              {user.phone ||
                                                                "—"}{" "}
                                                              | 👤{" "}
                                                              {user.username ||
                                                                "—"}
                                                            </p>

                                                          </div>
                                                        ),
                                                      )
                                                  ) : (
                                                    <p className="p-3 text-red-500 text-sm">
                                                      No users
                                                      found
                                                    </p>
                                                  )}

                                                </div>
                                              )}

                                          </div>
                                        )}

                                        {/* TEST ACTIONS */}

                                        <div className="flex gap-2 mt-3">

                                          <button
                                            disabled={
                                              isCompleted ||
                                              isNotified ||
                                              !selectedUser
                                            }
                                            onClick={async () => {

                                              if (
                                                isCompleted
                                              )
                                                return;

                                              await handleNotifyAndSave(
                                                {
                                                  appointmentId:
                                                    appt.appointmentId,
                                                  testRecommendation:
                                                    {
                                                      ...test,
                                                      OtherMedicalUser:
                                                        selectedUser,
                                                    },
                                                  testKey,
                                                },
                                              );

                                              setNotifiedTests(
                                                (
                                                  prev,
                                                ) => ({
                                                  ...prev,
                                                  [testKey]:
                                                    true,
                                                }),
                                              );

                                            }}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold text-white transition ${
                                              isCompleted ||
                                              isNotified
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-emerald-600 hover:bg-emerald-700"
                                            }`}
                                          >
                                            {isCompleted
                                              ? "Locked"
                                              : isNotified
                                                ? "Notified"
                                                : "Notify"}
                                          </button>

                                          <button
                                            disabled={
                                              isCompleted
                                            }
                                            onClick={() => {

                                              if (
                                                isCompleted
                                              )
                                                return;

                                              handleCancelTestRecommendation(
                                                appt.appointmentId,
                                                test.testid,
                                              );

                                            }}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold text-white ${
                                              isCompleted
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-red-500 hover:bg-red-600"
                                            }`}
                                          >
                                            Cancel
                                          </button>

                                        </div>

                                      </div>
                                    );
                                  },
                                )}

                              </div>
                            </div>
                          )}

                          {/* =================================================
                              MAIN ACTIONS
                          ================================================= */}

                          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#dfe7ef]">

                            <button
                              onClick={() =>
                                handleAddPrescription(
                                  appt.appointmentId,
                                )
                              }
                              className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition"
                            >
                              Add Prescription
                            </button>

                            <select
                              value={
                                appt.status ||
                                "pending"
                              }
                              onChange={(e) =>
                                handleStatusChange(
                                  appt.appointmentId,
                                  e.target.value,
                                )
                              }
                              className="px-3 py-2 rounded-lg border border-[#ccd9e6] bg-white text-[#31506d] text-xs font-medium outline-none focus:ring-2 focus:ring-sky-100"
                            >

                              <option value="pending">
                                Pending
                              </option>

                              <option value="confirmed">
                                Confirmed
                              </option>

                              <option value="in-progress">
                                In Progress
                              </option>

                              <option value="completed">
                                Completed
                              </option>

                            </select>

                            <button
                              onClick={() =>
                                handleCancelAppointment(
                                  appt.appointmentId,
                                )
                              }
                              className="px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-semibold transition"
                            >
                              Cancel
                            </button>

                            <button
                              onClick={() => {

                                handleAddTestRecommendation(
                                  appt.appointmentId,
                                );

                                fetchRegisteredUsers(
                                  "Other Medical Services",
                                );

                              }}
                              className="px-3 py-2 rounded-lg bg-[#7047a8] hover:bg-[#603a91] text-white text-xs font-semibold transition"
                            >
                              Refer Test
                            </button>

                            <button
                              onClick={() =>
                                handleSaveToDB(appt)
                              }
                              className="px-3 py-2 rounded-lg bg-[#193a5c] hover:bg-[#102d49] text-white text-xs font-semibold transition"
                            >
                              Save
                            </button>

                          </div>

                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            )}

          </div>
        );
      })}

      {/* ========================================================
          EMPTY STATE
      ======================================================== */}

      {groupedData.length === 0 && (
        <div className="bg-white border border-[#d5e0eb] rounded-2xl py-16 text-center shadow-sm">

          <div className="w-12 h-12 mx-auto rounded-full bg-[#eaf1f8] flex items-center justify-center">
            <FaUser className="text-[#4f6f8e]" />
          </div>

          <h3 className="mt-4 text-base font-semibold text-[#27425d]">
            No appointments
          </h3>

          <p className="text-sm text-[#8193a6] mt-1">
            There are no appointments available.
          </p>

        </div>
      )}

    </section>
  );
};

export default AppointmentsPage;
